import { applyFilterUnion } from '@warebee/shared/athena-db-model';
import {
  FilterFieldConfigBase,
  FilterPreset,
  getKyselyFilterDescSet,
  KeysMatching,
} from '@warebee/shared/common';
import {
  SortDirection,
  StringSearchFilter,
  StringSearchFilterType,
} from '@warebee/shared/data-access-api-dto';
import {
  ASSIGNMENT_MAPPING_SCHEMA,
  ITEM_SET_MAPPING_SCHEMA,
  ITEM_SET_UOM_FIELDS,
  LAYOUT_LOCATION_MAPPING_SCHEMA,
} from '@warebee/shared/import-converter';
import { sql } from 'kysely';
import _ from 'lodash';
import {
  AssignmentDataRow,
  getAssignmentQueryBuilder,
} from '../assignment/assignmentsQueryBuilder';
import { DatasetQueryBuilderParams } from '../datasetQueryBuilder.types';
import {
  getItemsQueryBuilder,
  ItemDataRow,
} from '../itemSet/itemsQueryBuilder';
import {
  getLocationQueryBuilder,
  LocationDataRow,
} from '../layoutLocations/locationsQueryBuilder';
import {
  orderSetStatsQueryBuilder,
  orderStatsDataColumnsAll,
  OrderStatsDataRow,
} from '../orderSet/orderSetStatsQueryBuilder';

// const simulationHqDummyQuery = getSimulationHqQueryBuilder({} as any)
//   .selectFrom('hq')
//   .selectAll();

// export type SimulationHqDataRows = InferResult<typeof simulationHqDummyQuery>;
// export type SimulationHqDataRow = SimulationHqDataRows[number];

export type SimulationHqDataRowBase = LocationDataRow &
  ItemDataRow &
  AssignmentDataRow &
  OrderStatsDataRow & {
    stockUomUom: string;
    stockUomLowerUom: string;
    stockUomUnitsPerLowestUom: number;
    stockUomNetWeight: number;
    stockUomLength: number;
    stockUomWidth: number;
    stockUomHeight: number;
    stockUomVolume: number;
    stockUomEan: string;
    stockUomUpc: string;
    stockUomMaxStack: number;
  };

export type SimulationHqDataRow = Omit<
  SimulationHqDataRowBase,
  'importJobId' | 'raw_data' | 'shape' | 'portals'
>;

export type SimulationHqDataRows = SimulationHqDataRow[];

export type SimulationHqDataColumn = keyof SimulationHqDataRow;

export type SimulationHqSortBy = {
  direction?: SortDirection;
  field: SimulationHqDataColumn;
};

export type SimulationHqFieldWithStringFilter = KeysMatching<
  SimulationHqDataRow,
  string
>;

export type SimulationHqFilterBy = Partial<
  Record<SimulationHqFieldWithStringFilter, StringSearchFilter>
>;

export type SimulationHqQueryBuilder = ReturnType<
  typeof getSimulationHqQueryBuilder
>;

export type SimulationHqQueryBuilderParams = {
  layout: DatasetQueryBuilderParams;
  itemSet: DatasetQueryBuilderParams;
  assignment: DatasetQueryBuilderParams;
  orderSet: DatasetQueryBuilderParams;
  filterPreset?: FilterPreset<SimulationHqDataColumn>;
  filterConfig?: FilterFieldConfigBase<SimulationHqDataColumn>[];
  filterBy?: SimulationHqFilterBy;
};

export function getSimulationHqQueryBuilder(
  params: SimulationHqQueryBuilderParams,
) {
  let builder = orderSetStatsQueryBuilder(params.orderSet)
    .with(
      cte => cte('is'),
      db => {
        return getItemsQueryBuilder(params.itemSet, db).where(
          ({ eb, or, ref }) =>
            or([
              eb('lowerUom', 'is', null),
              eb('lowerUom', '=', sql.raw<string>("''")),
              eb('lowerUom', '=', ref('uom')),
            ]),
        );
      },
    )
    .with(
      cte => cte('stockItem'),
      db => {
        return getItemsQueryBuilder(params.itemSet, db);
      },
    )

    .with(
      cte => cte('l'),
      db => {
        return getLocationQueryBuilder(params.layout, db);
      },
    )
    .with(
      cte => cte('a'),
      db => {
        return getAssignmentQueryBuilder(params.assignment, db);
      },
    )
    .with(
      cte => cte('hqRaw'),
      db => {
        return db
          .selectFrom('is')
          .fullJoin('a', join => join.onRef('a.skuKey', '=', 'is.skuKey'))
          .leftJoin('stockItem', join =>
            join.on(eb =>
              eb.and([
                eb('a.skuKey', '=', eb.ref('stockItem.skuKey')),
                eb('a.stockUom', '=', eb.ref('stockItem.uom')),
              ]),
            ),
          )
          .fullJoin('item_stats', join =>
            join.on(
              eb => eb.fn.coalesce('a.skuKey', 'is.skuKey'),
              '=',
              eb => eb.ref('item_stats.skuKey'),
            ),
          )
          .fullJoin('l', join =>
            join.onRef('a.locationId', '=', 'l.locationId'),
          )
          .select(eb => [
            eb.fn.coalesce('a.locationId', 'l.locationId').as('locationId'),
            eb.fn.coalesce('a.skuKey', 'is.skuKey').as('skuKey'),
            eb.fn.coalesce('a.consignee', 'is.consignee').as('consignee'),
            eb.fn.coalesce('a.sku', 'is.sku').as('sku'),
            'l.locationId as layoutLocationId',
            'assignmentItem',
            eb('a.locationId', '=', eb.ref('l.locationId')).as('matchAL'),
            eb
              .case()
              .when(
                eb.and([
                  eb('item_stats.categoryABC', 'is', null),
                  eb('a.locationId', 'is not', null),
                ]),
              )
              .then(sql.raw<string>("'D'"))
              .else(eb.ref('item_stats.categoryABC'))
              .end()
              .as('categoryABC'),
            // sql.raw(null).as('datasetObjectId'),
          ])
          .select(
            _(ITEM_SET_MAPPING_SCHEMA.fields)
              .map(f => f.name)
              .filter(c => !_.includes(['consignee', 'sku'], c))
              .map(c => sql.ref(`is.${c}`).as(c))
              .value(),
          )
          .select(
            _(ITEM_SET_MAPPING_SCHEMA.fields)
              .map(f => f.name)
              .filter(c => _.includes(ITEM_SET_UOM_FIELDS, c))
              .map(c =>
                sql.ref(`stockItem.${c}`).as(`stockUom${_.upperFirst(c)}`),
              )
              .value(),
          )
          .select(
            _(ASSIGNMENT_MAPPING_SCHEMA.fields)
              .map(f => f.name)
              .filter(c => !_.includes(['consignee', 'sku', 'locationId'], c))
              .value(),
          )
          .select(
            _(LAYOUT_LOCATION_MAPPING_SCHEMA.fields)
              .map(f => f.name)
              .filter(
                c => c !== 'locationId' && c !== 'shape' && c !== 'portals',
              )

              .value(),
          )
          .select(orderStatsDataColumnsAll.filter(f => f !== 'categoryABC'));
        // .select([
        //   'stockItem.unitsPerLowestUom as stockUomUnitsPerLowestUom',
        //   'stockItem.netWeight as stockUomNetWeight',
        //   'stockItem.length as stockUomLength',
        //   'stockItem.width as stockUomWidth',
        //   'stockItem.height as stockUomHeight',
        //   'stockItem.volume as stockUomVolume',
        //   'stockItem.ean as stockUomEan',
        //   'stockItem.upc as stockUomUpc',
        //   'stockItem.maxStack as stockUomMaxStack',
        // ]);
      },
    )
    .with(
      cte => cte('hq'),
      db => {
        let builder = db.selectFrom('hqRaw').selectAll();

        if (params.filterBy) {
          builder = _.reduce(
            params.filterBy,
            (q, value, key) => {
              if (
                value?.type === StringSearchFilterType.CONTAINS &&
                value?.value === 'undefined'
              ) {
                return q.where(key as any, 'is', null);
              }

              if (
                value?.type === StringSearchFilterType.CONTAINS &&
                !_.isEmpty(value?.value)
              ) {
                return q.where(key as any, 'like', `%${value.value}%`);
              }

              return q;
            },
            builder,
          );
        }

        if (!_.isEmpty(params.filterPreset?.filterUnion?.anyOf)) {
          const [filterBuilder, exp] = applyFilterUnion(
            builder,
            getKyselyFilterDescSet(params.filterConfig),
            params.filterPreset.filterUnion,
          );

          builder = filterBuilder.where(exp);
        }

        return builder;
      },
    );

  return builder;
}
